import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  Pagination,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import GenericButton from "../../components/Custom/GenericButton";
import api from "../../globals/axios";
import { useAlert } from "../../components/Alert";
import { useTranslation } from "react-i18next";
import CustomAppBar from "../../components/AppBar";
const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    minHeight: "100vh",
    marginBottom: "-4rem",
  },
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  title: {
    textAlign: "center",
    padding: "20px 0",
    borderRadius: "4px",
    marginBottom: "20px",
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  filterContainer: {
    display: "flex",
    gap: "16px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  tableContainer: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    padding: "10px",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 15px",
    marginBottom: "5px",
    borderRadius: "6px",
    backgroundColor: "#f9f9f9",
  },
  headerRow: {
    fontWeight: "bold",
    backgroundColor: "#FFC107",
    color: "#fff",
    padding: "10px 15px",
    borderRadius: "6px",
  },
  cell: {
    flex: 1,
    textAlign: "center",
  },
}));

const ResetPasswordUsers = () => {
  const classes = useStyles();
  const { t } = useTranslation();
    // Internal login for sub-users under Head
    const handleInternalLogin = async (id) => {
      try {
        // Get current auth data from local storage
        const currentAuthData = JSON.parse(localStorage.getItem('auth'));
        const response = await api.post(`/vle/farmer-login/?persona_user=${id}`);
        const authData = JSON.stringify(response.data);

        if (currentAuthData && currentAuthData.is_internal_login) {
          window.location.replace(`/login-internal/?authDetails=${authData}`);
        } else {
          if (currentAuthData) {
            localStorage.setItem('auth_backup', JSON.stringify({
              ...currentAuthData,
              redirectionURL: window.location.href,
            }));
          }
          localStorage.setItem('auth', authData);
          window.location.replace(`/login-internal/?authDetails=${authData}`);
        }
      } catch (err) {
        console.error(err);
        showAlert('error', 'Error logging in as sub-user');
      }
    };
  const [subusers, setSubusers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    total_pages: 0,
    total_objects: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    username: "",
    fullname: "",
    role: "",
    phone_number: "",
  });
  const { showAlert } = useAlert();

  // Fetch farmers data
  const fetchSubusersData = (page = 1) => {
    setIsLoading(true);
    let { username, fullname, role, phone_number } = filters;
    let query = `?page=${page}`;
    if (username) query += `&username=${username}`;
    if (fullname) query += `&fullname=${fullname}`;
    if (role) query += `&role=${role}`;
    if (phone_number) query += `&phone_number=${phone_number}`;

    let url = `/app-admin/get-admin-users-list/${query}`;

    api
      .get(url)
      .then((res) => {
        if (res && res.data) {
          setSubusers(res.data.results);
          setPagination({
            page: res.data.current_page_number,
            total_pages: res.data.total_pages,
            total_objects: res.data.total_objects,
          });
        }
        setError("");
      })
      .catch((err) => {
        console.error(err);
        setError("Couldn't fetch the farmers data. Please try again later!");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePageChange = (event, value) => {
    fetchSubusersData(value);
  };

  useEffect(() => {
    fetchSubusersData();
  }, []);

  const handleResetpassword = (userId) => {
    api
      .post(`/app-admin/reset-user-password/${userId}/`)
      .then((res) => {
        if (res.status === 200) {
          showAlert("success", "Password reset successfully!");
        }
      })
      .catch((error) => {
        showAlert(
          "Error resetting password:",
          error.response?.data || error.message
        );
      });
  };

  const roleOptions = [
    { value: "FARMER", label: "Farmer" },
    { value: "FPO", label: "FPO" },
    { value: "AUDITOR", label: "Auditor" },
    { value: "FPO_AGGREGATOR", label: "FPO Aggregator" },
    { value: "BUYER", label: "Buyer" },
    { value: "SPONSOR", label: "Sponsor" },
    { value: "VERIFIER", label: "Verifier" },
    { value: "AGRO_EXPERT", label: "Agro Expert" },
    { value: "SERVICE_PROVIDER", label: "Service Provider" },
    { value: "PROGRAM_MANAGER", label: "Program Manager" },
    { value: "POP_MANAGER", label: "POP Manager" },
    { value: "VLE", label: "VLE" },
    { value: "AREA_MANAGER", label: "Area Manager" },
    { value: "ADMIN", label: "Admin" },
  ];

  return (
    <Box className={classes.root} component={Paper}>
      <CustomAppBar />

      <Box className={classes.container}>
        <Paper className={classes.title} elevation={0}>
          <Typography variant="h5">{t("Password Reset Dashboard")}</Typography>
        </Paper>

        <Box className={classes.filterContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={t("Username")}
                value={filters.username}
                onChange={(e) =>
                  setFilters({ ...filters, username: e.target.value })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={t("Full Name")}
                value={filters.fullname}
                onChange={(e) =>
                  setFilters({ ...filters, fullname: e.target.value })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={t("Phone Number")}
                value={filters.phone_number}
                onChange={(e) =>
                  setFilters({ ...filters, phone_number: e.target.value })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="role-mapped-label">{t("Role")}</InputLabel>
                <Select
                  labelId="role-label"
                  value={filters.role}
                  label={t("Role")}
                  onChange={(e) =>
                    setFilters({ ...filters, role: e.target.value })
                  }
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {t(role.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <GenericButton
                backgroundColor={"#4CAF50"}
                fontColor={"#fff"}
                fontSize={"14px"}
                fullWidth
                padding={"15px 10px"}
                onClick={() => fetchSubusersData()}
              >
                {t("Search")}
              </GenericButton>
            </Grid>
          </Grid>
        </Box>

        <Box className={classes.tableContainer} sx={{ overflowX: "auto" }}>
          <Box className={`${classes.row} ${classes.headerRow}`}>
            <Typography className={classes.cell}>{t("User Name")}</Typography>
            <Typography className={classes.cell}>{t("Full Name")}</Typography>
            <Typography className={classes.cell}>{t("Role")}</Typography>
            <Typography className={classes.cell}>
              {t("Phone Number")}
            </Typography>
            <Typography className={classes.cell}></Typography>
          </Box>

          {isLoading ? (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" align="center">
              {error}
            </Typography>
          ) : subusers.length === 0 ? (
            <Typography align="center" sx={{ padding: "10px" }}>
              {t("No Users Found")}
            </Typography>
          ) : (
            subusers.map((user, index) => (
              <Box key={index} className={classes.row}>
                <Typography className={classes.cell} noWrap>
                  {user.username || "-"}
                </Typography>
                <Typography className={classes.cell} noWrap>
                  {user.fullname || "-"}
                </Typography>
                <Typography className={classes.cell} noWrap>
                  {user.role || "-"}
                </Typography>
                <Typography className={classes.cell} noWrap>
                  {user.phone_number || "-"}
                </Typography>
                <Box className={classes.cell}>
                  <GenericButton
                    backgroundColor={"#4CAF50"}
                    fontColor={"#fff"}
                    fontSize={"12px"}
                    padding={"5px 10px"}
                    onClick={() => handleResetpassword(user.id)}
                  >
                    {t("Reset Password")}
                  </GenericButton>
                    <GenericButton
                      backgroundColor={"#1976D2"}
                      fontColor={"#fff"}
                      fontSize={"12px"}
                      padding={"5px 10px"}
                      style={{ marginLeft: '8px' }}
                      onClick={() => handleInternalLogin(user.id)}
                    >
                      {t("Internal Login")}
                    </GenericButton>
                </Box>
              </Box>
            ))
          )}
        </Box>
        <Box
          className={classes.pagination}
          sx={{ display: "flex", justifyContent: "center", mt: 2 }}
        >
          <Pagination
            count={pagination.total_pages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            disabled={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPasswordUsers;
